import { useState, useRef, useEffect } from 'react';
import {
  Training,
  Theme,
  Evaluation,
  Flag,
  DuoToneCourseFilled,
  DuoToneAssessmentFilled,
  DuoToneVacancyFilled,
  InformationFilled,
} from '@teo/components/icons';
import { Button, IconTile, ProgressBar } from '@teo/components';
import { useGet } from '@/query/fetchHooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { nthLastIndexOf, capitalizeFirst } from '@/util';
import { Blocked } from '@/components/Images/Blocked';
import { axios } from '@/axios';
import { config } from '@/config';
import { categoriesIcon, sortDataLP } from '@/components/LearningPath/util';

const BlockIcon = ({ Icon, className }) => (
  <div className={'rounded bg-grey-01 p-4'}>
    <Icon className={className} />
  </div>
);

export const ActiveCard = ({
  selectNode,
  auth,
  allCategories,
  items,
  follow,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setDate] = useState(null);
  const [followCourse, setFollowCourse] = useState(null);
  const [isUserFollow, setIsUserFollow] = useState(true);
  const [courseInfo, setCourseInfo] = useState(null);
  const [themeProgress, setThemeProgress] = useState(0);
  const [statusCard, setStatusCard] = useState({
    isCorrected: false,
    isPassed: false,
  });
  const originPath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 2)
  );

  const [milestoneNotBlock, setMilestoneNotBlock] = useState(false);

  const [firstParentNotPass, setFirstParentNotPass] = useState(null);

  const basePath = window.location.pathname;

  useGet(`/coursedetail/${selectNode?.objectId}`, {
    enabled: selectNode?.type === 'course',
    onSuccess: async (res) => {
      setDate(res?.data || null);
    },
  });

  useGet(`/follow/${selectNode?.objectId}/${auth?.user?.id}`, {
    enabled: selectNode?.type === 'course' && selectNode?.notBlock,
    retry: false,
    onSuccess: async (res) => {
      setFollowCourse(res?.data || null);
      setIsUserFollow(true);
    },
    onError: (res) => {
      console.error('res', res);
      setIsUserFollow(false);
    },
  });

  useGet(`/courseinfo/${selectNode?.objectId}`, {
    enabled: selectNode?.type === 'course',
    retry: false,
    onSuccess: (result) => {
      setCourseInfo(result?.data || {});
    },
  });

  useGet(`/trainingfollow/${selectNode?.objectId}/${auth?.user?.id}`, {
    enabled: selectNode?.type === 'training' && selectNode?.notBlock,
    retry: false,
    onSuccess: async (res) => {
      setFollowCourse(res?.data || null);
      setIsUserFollow(true);
    },
    onError: (res) => {
      console.error('res', res);
      setIsUserFollow(false);
    },
  });

  useGet(`/training/${selectNode?.objectId}`, {
    enabled: selectNode?.type === 'training',
    onSuccess: async (res) => {
      setDate(res?.data || null);
    },
  });

  useGet(`/learningpath/${selectNode?.objectId}`, {
    enabled: selectNode?.type === 'learningpath',
    onSuccess: async (res) => {
      setDate(res?.data || null);
    },
  });

  useGet(`/lpnodestatuses`, {
    enabled: selectNode?.type === 'learningpath',
    lpgroupId: selectNode?.lpgroupId,
    learningpathId: selectNode?.objectId,
    userId: auth?.user?.id,
    onSuccess: async (res) => {
      const nodedetails = res?.data?.filter((el) => el?.type !== 'root');
      const nodesDone = nodedetails?.filter(
        (el) =>
          el.progress == 1 ||
          (el.type === 'course' && el.competenciesConditionsMet)
      );
      const progress = nodesDone?.length / nodedetails?.length;
      setThemeProgress(progress);
    },
  });

  let Icon;
  if (selectNode?.tags && selectNode?.type === 'learningpath') {
    const learningpathTags = allCategories?.find(
      (el) => el?.label === selectNode?.tags.split(':')[1]
    );
    Icon = categoriesIcon(learningpathTags?.icon);
  } else {
    Icon = Theme;
  }

  function getAllParentsById(items, nodeId) {
    const byId = new Map(items.map((item) => [item.id, item]));
    const start = items.find((item) => item.id === nodeId);

    if (!start) return [];

    const result = [];
    const visited = new Set();

    function walk(node) {
      if (!node?.roots?.length) return;

      for (const parentId of node.roots) {
        if (visited.has(parentId)) continue;
        visited.add(parentId);

        const parent = byId.get(parentId);
        if (parent) {
          result.push(parent);
          walk(parent);
        }
      }
    }

    walk(start);
    return result;
  }

  // function areAllPassed(arr) {
  //   return arr.every((item) => {
  //     const hasProgress = item.progress === 1;
  //     const hasScore =
  //       item.maxScore === 0 && item.score === 0 && item?.isCorrected
  //         ? true
  //         : item.minScore > 0
  //         ? item.score / item.maxScore >= item?.minScore
  //         : true;
  //     const statusArr = {
  //       allDone: hasProgress,
  //       allPassed: hasProgress && hasScore && item?.isCorrected,
  //     };
  //     console.log(statusArr);
  //     return statusArr;
  //   });
  // }
  function areAllPassed(arr) {
    return arr.reduce(
      (acc, item) => {
        const hasProgress = item.progress === 1;

        const hasScore =
          item.maxScore === 0 && item.score === 0 && item?.isCorrected
            ? true
            : item.minScore > 0
            ? item.score / item.maxScore >= item?.minScore
            : true;

        const allPassedForItem =
          !!item?.skippedByCompetences ||
          (hasProgress && hasScore && item?.isCorrected);

        return {
          allDone: acc.allDone && (hasProgress || !!item?.skippedByCompetences),
          allPassed: acc.allPassed && allPassedForItem,
        };
      },
      { allDone: true, allPassed: true }
    );
  }

  useEffect(() => {
    const cardStatus =
      selectNode?.progress === 1
        ? selectNode.maxScore > 0
          ? selectNode.score / selectNode.maxScore >= selectNode.minScore
            ? {
                isCorrected: true,
                isPassed: true,
              }
            : {
                isCorrected: true,
                isPassed: false,
              }
          : selectNode?.isCorrected
          ? {
              isCorrected: true,
              isPassed: true,
            }
          : {
              isCorrected: false,
              isPassed: false,
            }
        : {
            isCorrected: true,
            isPassed: true,
          };
    setStatusCard(cardStatus);
    if (selectNode?.subType === 'milestone') {
      const allBefore = getAllParentsById(items, selectNode?.id);
      const allOk = areAllPassed(allBefore);
      setMilestoneNotBlock(allOk);
    }
  }, [selectNode]);

  function getAllParentsByObjectId(items, targetObjectId) {
    const byId = new Map(items.map((item) => [item.id, item]));
    const start = items.find((item) => item.id === targetObjectId);

    if (!start) return [];

    const result = [];
    const visited = new Set();

    function walk(node) {
      if (!node?.roots?.length) return;

      for (const parentId of node.roots) {
        if (visited.has(parentId)) continue;
        visited.add(parentId);

        const parent = byId.get(parentId);
        if (parent) {
          result.push(parent);
          walk(parent);
        }
      }
    }

    walk(start);
    return result;
  }

  const chaeckScore = (nds) => {
    if (nds.notPassed === true) {
      const hasProgress = nds.progress === 1;
      const enoughScore =
        nds.maxScore > 0
          ? nds.score / nds.maxScore < nds.minScore
          : nds?.isCorrected
          ? false
          : true;
      return hasProgress && enoughScore;
    }

    return false;
  };

  useEffect(() => {
    const allParent = getAllParentsByObjectId(items, selectNode?.id);
    const sortData = sortDataLP(allParent);
    const lastActive = sortData?.find(
      (nds) =>
        (nds.progress !== 1 || chaeckScore(nds)) &&
        nds?.mandatory &&
        nds?.subType !== 'milestone' &&
        nds?.blocking &&
        (nds?.notCompleted || nds?.notPassed)
    );
    const lastCompleatedActive =
      sortData[0]?.progress === 1
        ? sortData[sortData?.length - 1]
        : sortData[0];
    setFirstParentNotPass(
      lastActive
        ? lastActive
        : lastCompleatedActive
        ? lastCompleatedActive
        : sortData[0]
    );
  }, [selectNode, items]);

  if (!selectNode) return null;

  return (
    <div
      className="absolute bottom-8 left-1/2 z-[1] w-full max-w-[720px] px-4"
      style={{
        transform: 'translate(-50%, 0)',
      }}
    >
      <div className="flex w-full max-w-[720px] rounded-xl bg-white p-5 drop-shadow-m">
        <div className="pr-5">
          {selectNode?.notBlock ? (
            <IconTile
              Icon={
                selectNode.subType === 'training'
                  ? Training
                  : selectNode.subType === 'learningpath'
                  ? Icon
                  : selectNode.subType === 'milestone'
                  ? Flag
                  : selectNode.subType === 'evaluation'
                  ? Evaluation
                  : selectNode.subType === 'course'
                  ? DuoToneCourseFilled
                  : selectNode.subType === 'assessment'
                  ? DuoToneAssessmentFilled
                  : selectNode.subType === 'leervacature'
                  ? DuoToneVacancyFilled
                  : DuoToneCourseFilled
              }
              imageUrl={selectNode.picture}
              color={
                selectNode.subType === 'training'
                  ? 'green'
                  : selectNode.subType === 'learningpath'
                  ? 'dark_blue'
                  : selectNode.subType === 'milestone'
                  ? 'blue'
                  : selectNode.subType === 'evaluation'
                  ? 'purple'
                  : selectNode.subType === 'course'
                  ? 'yellow'
                  : selectNode.subType === 'assessment'
                  ? 'red'
                  : selectNode.subType === 'leervacature'
                  ? 'yellow'
                  : 'yellow'
              }
              size="small_lg"
              className={
                selectNode.subType === 'course' ||
                selectNode.subType === 'import'
                  ? 'fill-primary-06 text-primary-01'
                  : selectNode.subType === 'assessment'
                  ? 'fill-tint-red-dark text-tint-red-light'
                  : selectNode.subType === 'leervacature'
                  ? 'fill-error-04 text-error-04'
                  : ''
              }
            />
          ) : (
            <BlockIcon
              Icon={
                selectNode.subType === 'training'
                  ? Training
                  : selectNode.subType === 'learningpath'
                  ? Theme
                  : selectNode.subType === 'milestone'
                  ? Flag
                  : selectNode.subType === 'evaluation'
                  ? Evaluation
                  : selectNode.subType === 'course'
                  ? DuoToneCourseFilled
                  : selectNode.subType === 'assessment'
                  ? DuoToneAssessmentFilled
                  : selectNode.subType === 'leervacature'
                  ? DuoToneVacancyFilled
                  : DuoToneCourseFilled
              }
              className={`h-8 w-8 ${
                selectNode?.type === 'course'
                  ? selectNode.subType === 'leervacature'
                    ? 'fill-grey-04 text-grey-04'
                    : 'fill-grey-04 text-grey-01'
                  : 'text-grey-04'
              }`}
            />
          )}
        </div>
        <div className="flex w-full flex-col">
          <div className="flex items-start">
            <div className="flex flex-1 flex-col gap-1">
              <p className="text-sm text-grey-05">
                {selectNode?.subType === 'training'
                  ? t('pages.learning_path.training')
                  : selectNode?.subType === 'learningpath'
                  ? t('pages.learning_path.theme')
                  : selectNode?.subType === 'milestone'
                  ? t('pages.learning_path.milestone')
                  : selectNode?.subType === 'evaluation'
                  ? t('pages.learning_path.evaluation')
                  : selectNode?.subType === 'course'
                  ? t('links.course')
                  : selectNode?.subType === 'assessment'
                  ? t('links.assessment')
                  : selectNode?.subType === 'leervacature'
                  ? t('links.vacancy')
                  : t('links.course')}
              </p>
              <p className={` text-left text-base font-semibold text-black  `}>
                {selectNode?.name}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={!selectNode?.notBlock || follow?.isBlocked}
              onClick={() => {
                if (selectNode?.type === 'course') {
                  if (!isUserFollow) {
                    const promises = [];
                    promises.push(
                      axios.post(`${config.backend}/follows`, {
                        courseId: selectNode?.objectId,
                        userId: auth?.user?.id,
                        role: 20,
                      })
                    );
                    Promise.allSettled(promises).then(() => {
                      navigate(
                        `${originPath}/${
                          selectNode?.subType === 'assessment'
                            ? 'assessments'
                            : selectNode?.subType === 'leervacature'
                            ? 'vacancies'
                            : 'courses'
                        }/${selectNode?.objectId}`
                      );
                    });
                  } else {
                    navigate(
                      `${originPath}/${
                        selectNode?.subType === 'assessment'
                          ? 'assessments'
                          : selectNode?.subType === 'leervacature'
                          ? 'vacancies'
                          : 'courses'
                      }/${selectNode?.objectId}`
                    );
                  }
                }

                if (selectNode?.type === 'training') {
                  if (!isUserFollow) {
                    const promises = [];
                    promises.push(
                      axios.post(`${config.backend}/trainingfollows`, {
                        trainingId: selectNode?.objectId,
                        userId: auth?.user?.id,
                      })
                    );
                    Promise.allSettled(promises).then(() => {
                      navigate(
                        `${basePath}/${
                          selectNode?.subType === 'milestone'
                            ? 'milestones'
                            : selectNode?.subType === 'evaluation'
                            ? 'evaluations'
                            : 'trainings'
                        }/${selectNode?.objectId}`
                      );
                    });
                  } else {
                    navigate(
                      `${basePath}/${
                        selectNode?.subType === 'milestone'
                          ? 'milestones'
                          : selectNode?.subType === 'evaluation'
                          ? 'evaluations'
                          : 'trainings'
                      }/${selectNode?.objectId}`
                    );
                  }
                }
                selectNode?.type === 'learningpath' &&
                  navigate(`${basePath}/themes/${selectNode?.objectId}`);
              }}
            >
              {selectNode?.subType === 'training'
                ? t('pages.lp_landing.view_training')
                : selectNode?.subType === 'learningpath'
                ? t('pages.lp_landing.view_theme')
                : selectNode?.subType === 'milestone'
                ? t('pages.lp_landing.view_milestone')
                : selectNode?.subType === 'evaluation'
                ? t('pages.lp_landing.view_evaluation')
                : selectNode?.subType === 'course'
                ? t('pages.lp_landing.view_course')
                : selectNode?.subType === 'assessment'
                ? t('pages.lp_landing.view_assessment')
                : selectNode?.subType === 'leervacature'
                ? t('pages.lp_landing.view_vacancy')
                : t('pages.lp_landing.view_course')}
            </Button>
          </div>

          {selectNode?.type === 'course' &&
            selectNode?.notBlock &&
            selectNode?.skippedByCompetences && (
              // competence conditions met: shown as done (100%)
              <div className="my-2 flex items-center">
                <div className="flex w-full">
                  <ProgressBar
                    max={100}
                    value={100}
                    className="progress-bar__all h-1 overflow-hidden rounded-lg"
                  />
                </div>
                <span className="pl-3 text-xs text-grey-06">100%</span>
              </div>
            )}
          {selectNode?.type === 'course' &&
            selectNode?.notBlock &&
            !selectNode?.skippedByCompetences && (
              <div className="my-2 flex items-center">
                <div className="flex w-full">
                  <ProgressBar
                    max={100}
                    value={
                      selectNode?.progress && isUserFollow
                        ? Math.round(selectNode?.progress * 100)
                        : 0
                    }
                    className={`h-1 overflow-hidden rounded-lg ${
                      followCourse?.progress * 100 > 99 && 'progress-bar__all'
                    }`}
                  />
                </div>
                <span className="pl-3 text-xs text-grey-06">
                  {followCourse?.progress && isUserFollow
                    ? Math.round(followCourse?.progress * 100)
                    : 0}
                  %
                </span>
              </div>
            )}
          {selectNode?.type === 'learningpath' && selectNode?.notBlock && (
            <div className="my-2 flex items-center">
              <div className="flex w-full">
                <ProgressBar
                  max={100}
                  value={themeProgress ? Math.round(themeProgress * 100) : 0}
                  className={`h-1 overflow-hidden rounded-lg ${
                    themeProgress * 100 > 99 && 'progress-bar__all'
                  }`}
                />
              </div>
              <span className="pl-3 text-xs text-grey-06">
                {themeProgress ? Math.round(themeProgress * 100) : 0}%
              </span>
            </div>
          )}
          {selectNode?.notBlock && selectNode?.skippedByCompetences && (
            <div className="my-2">
              <div className="flex w-full flex-col gap-1 rounded-lg bg-tint-blue-light p-5">
                <InformationFilled className="h-6 w-6 text-tint-blue" />
                <p className="text-sm text-[#004795]">
                  {capitalizeFirst(
                    t('pages.learning_path.conditions.skip_card', {
                      subTypeDem: t(`types.${selectNode?.subType}Dem`),
                    })
                  )}
                </p>
              </div>
            </div>
          )}
          {selectNode?.notBlock &&
            !selectNode?.skippedByCompetences &&
            selectNode?.subType !== 'milestone' && (
              <>
                {statusCard?.isCorrected ? (
                  !statusCard?.isPassed && (
                    <div className="my-2">
                      <div className="flex w-full flex-col gap-1 rounded-lg bg-tint-blue-light p-5">
                        <InformationFilled className="h-6 w-6 text-tint-blue" />
                        <p
                          className="text-sm text-[#004795]"
                          dangerouslySetInnerHTML={{
                            __html: t('pages.lp_landing.notPassCard', {
                              subTypeDef: t(`types.${selectNode?.subType}Def`),
                            }),
                          }}
                        ></p>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="my-2">
                    <div className="flex w-full flex-col gap-1 rounded-lg bg-tint-blue-light p-5">
                      <InformationFilled className="h-6 w-6 text-tint-blue" />
                      <p
                        className="text-sm text-[#004795]"
                        dangerouslySetInnerHTML={{
                          __html: capitalizeFirst(
                            t('pages.lp_landing.notCorectCard', {
                              subTypeDem: t(`types.${selectNode?.subType}Dem`),
                            })
                          ),
                        }}
                      ></p>
                    </div>
                  </div>
                )}
              </>
            )}
          {!selectNode?.notBlock && selectNode?.subType !== 'milestone' && (
            <div className="my-2 flex w-full items-center rounded bg-grey-01 py-2 px-3">
              <div className="mx-2">
                <Blocked className="w-4 text-grey-08" />
              </div>
              <p
                className="pl-2 text-grey-08"
                dangerouslySetInnerHTML={{
                  __html: capitalizeFirst(
                    t('pages.lp_landing.blockCard', {
                      subTypeDem: t(`types.${selectNode?.subType}Dem`),
                      prevSubTypeDef: t(
                        `types.${firstParentNotPass?.subType}Def`
                      ),
                      subTypeDemRef: t(`types.${selectNode?.subType}DemRef`),
                      prevName: firstParentNotPass?.name,
                    })
                  ),
                }}
              ></p>
            </div>
          )}
          {selectNode?.subType === 'milestone' &&
            milestoneNotBlock?.allDone &&
            !milestoneNotBlock?.allPassed && (
              <div className="my-2">
                <div className="flex w-full flex-col gap-1 rounded-lg bg-tint-blue-light p-5">
                  <InformationFilled className="h-6 w-6 text-tint-blue" />
                  <p
                    className="text-sm text-[#004795]"
                    dangerouslySetInnerHTML={{
                      __html: t('pages.lp_landing.milestone_message'),
                    }}
                  ></p>
                </div>
              </div>
            )}
          {selectNode?.subType === 'milestone' &&
            !milestoneNotBlock?.allDone &&
            !milestoneNotBlock?.allPassed && (
              <div className="my-2 flex w-full items-center rounded bg-grey-01 py-2 px-3">
                <div className="mx-2">
                  <Blocked className="w-4 text-grey-08" />
                </div>
                <p
                  className="pl-2 text-grey-08"
                  dangerouslySetInnerHTML={{
                    __html: capitalizeFirst(
                      t('pages.lp_landing.blockCard', {
                        subTypeDem: t(`types.${selectNode?.subType}Dem`),
                        prevSubTypeDef: t(
                          `types.${firstParentNotPass?.subType}Def`
                        ),
                        subTypeDemRef: t(`types.${selectNode?.subType}DemRef`),
                        prevName: firstParentNotPass?.name,
                      })
                    ),
                  }}
                ></p>
              </div>
            )}
          <div className="flex">
            {selectNode?.type === 'course' && (
              <p
                className="text-sm text-grey-08 sm:text-base"
                dangerouslySetInnerHTML={{
                  __html:
                    data?.hasCustomLanding && courseInfo?.abstract
                      ? courseInfo?.abstract
                      : data?.description,
                }}
              />
            )}

            {selectNode?.type === 'training' && (
              <p
                className="text-sm text-grey-08 sm:text-base"
                dangerouslySetInnerHTML={{
                  __html: data?.trainingDescription,
                }}
              />
            )}

            {selectNode?.type === 'learningpath' && (
              <p
                className="text-sm text-grey-08 sm:text-base"
                dangerouslySetInnerHTML={{
                  __html: data?.lpDescription,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
